import { OpenImgContextProvider } from 'openimg/react'
import {
	data,
	Links,
	Meta,
	Outlet,
	redirect,
	Scripts,
	ScrollRestoration,
	useLoaderData,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { CookieConsentBanner } from './components/cookie-consent.tsx'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { ImpersonationBanner } from './components/impersonation-banner.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout, sessionKey } from './utils/auth.server.ts'
import {
	pickActiveMembership,
	rememberActiveOrganization,
} from './utils/organization-context.server.ts'
import { derivePlanState, planStateShortLabel } from './utils/plan-state.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import {
	getUserEffectivePermissions,
	userHasPermission as serverUserHasPermission,
} from './utils/permissions.service.ts'
import { authSessionStorage } from './utils/session.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{ title: data ? 'Vigilio' : 'Erreur | Vigilio' },
		{ name: 'description', content: `Your own captain's log` },
	]
}

// Organization select shape, reused for both normal and impersonation queries
const organizationSelect = {
	id: true,
	name: true,
	slug: true,
	// pickActiveMembership reads this to skip an organization being deleted.
	status: true,
	logoUrl: true,
	industrySector: true,
	teamSize: true,
	searchRegion: true,
	defaultLanguages: true,
	timezone: true,
	defaultSendTime: true,
	defaultSendDay: true,
	subscription: {
		select: {
			planType: true,
			status: true,
			trialEnd: true,
		},
	},
} as const

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	// Detect impersonation from session
	let isImpersonating = false
	let impersonatingOrgId: string | null = null
	let impersonatingOrgName: string | null = null
	let activeOrganizationId: string | null = null
	let currentSessionId: string | null = null

	if (userId) {
		const authSession = await authSessionStorage.getSession(
			request.headers.get('cookie'),
		)
		const sessionId = authSession.get(sessionKey)
		if (sessionId) {
			currentSessionId = sessionId
			const session = await prisma.session.findUnique({
				where: { id: sessionId },
				select: {
					impersonatingOrgId: true,
					activeOrganizationId: true,
				},
			})
			activeOrganizationId = session?.activeOrganizationId ?? null
			if (session?.impersonatingOrgId) {
				// Re-validate impersonation permission (uses cached permissions, 5-min TTL)
				const hasPermission = await serverUserHasPermission(
					userId,
					'impersonate:organization:any',
				)
				if (hasPermission) {
					impersonatingOrgId = session.impersonatingOrgId
					isImpersonating = true
				} else {
					// Permission revoked — clear impersonation
					await prisma.session.update({
						where: { id: sessionId },
						data: { impersonatingOrgId: null },
					})
				}
			}
		}
	}

	// Auto-clear impersonation when navigating to admin pages
	const url = new URL(request.url)
	if (isImpersonating && url.pathname.startsWith('/admin')) {
		const authSession = await authSessionStorage.getSession(
			request.headers.get('cookie'),
		)
		const sessionId = authSession.get(sessionKey)
		if (sessionId) {
			await prisma.session.update({
				where: { id: sessionId },
				data: { impersonatingOrgId: null },
			})
		}
		isImpersonating = false
		impersonatingOrgId = null
		impersonatingOrgName = null
	}

	const user = userId
		? await time(
			async () => {
				const userData = await prisma.user.findUnique({
					select: {
						id: true,
						name: true,
						username: true,
						timezone: true,
						roles: {
							select: {
								name: true,
								permissions: {
									select: { entity: true, action: true, access: true },
								},
							},
						},
						memberships: {
							// Same ordering as the resolver's fallback query, tie-break
							// included, so both pick the same row.
							orderBy: [{ joinedAt: 'asc' }, { id: 'asc' }],
							select: {
								organizationId: true,
								role: true,
								dailySummaryEnabled: true,
								instantAlertsEnabled: true,
								weeklyReportEnabled: true,
								preferredSendTime: true,
								organization: {
									select: organizationSelect,
								},
							},
						},
					},
					where: { id: userId },
				})

				if (!userData) return null

				// Get effective permissions (includes hierarchy + direct permissions)
				const effectivePermissions = await getUserEffectivePermissions(
					userData.id,
				)

				// A user can belong to several organizations. The session says
				// which one they are looking at; pickActiveMembership is pure so
				// this loader and the child loaders — which run in parallel and
				// resolve independently — cannot disagree about the answer.
				let membership = pickActiveMembership(
					userData.memberships,
					activeOrganizationId,
				)
				let organization = membership?.organization ?? null

				// Repair the session when it pointed nowhere, or at an
				// organization this user no longer belongs to.
				if (
					!isImpersonating &&
					currentSessionId &&
					membership &&
					membership.organizationId !== activeOrganizationId
				) {
					await rememberActiveOrganization(
						currentSessionId,
						membership.organizationId,
					)
				}

				// If impersonating, load the target org instead of the admin's own org
				if (isImpersonating && impersonatingOrgId) {
					const impersonatedOrg = await prisma.organization.findUnique({
						where: { id: impersonatingOrgId },
						select: organizationSelect,
					})
					if (impersonatedOrg) {
						impersonatingOrgName = impersonatedOrg.name
						organization = impersonatedOrg
						// Construct a virtual membership for the impersonated org
						// The admin gets OWNER-level access for the impersonated view
						membership = {
							organizationId: impersonatedOrg.id,
							role: 'OWNER' as const,
							dailySummaryEnabled: membership?.dailySummaryEnabled ?? true,
							instantAlertsEnabled: membership?.instantAlertsEnabled ?? true,
							weeklyReportEnabled: membership?.weeklyReportEnabled ?? true,
							preferredSendTime: membership?.preferredSendTime ?? '08:00',
							organization: impersonatedOrg,
						}
					} else {
						// Impersonated org no longer exists — clear impersonation
						isImpersonating = false
						impersonatingOrgId = null
						impersonatingOrgName = null
					}
				}

				return {
					id: userData.id,
					name: userData.name,
					username: userData.username,
					timezone: userData.timezone,
					roles: userData.roles,
					membership,
					organization,
					// Everything the switcher needs, and nothing more: shipping
					// the full organizationSelect for every membership would send
					// search regions and default languages the UI never reads.
					// Empty while impersonating — an admin viewing a tenant must
					// not be offered their own organizations.
					organizations: isImpersonating
						? []
						: userData.memberships.map((m) => ({
							id: m.organization.id,
							name: m.organization.name,
							slug: m.organization.slug,
							// The switcher offers a pending organization the
							// restore screen instead of a switch that no-ops.
							status: m.organization.status,
							role: m.role,
							planLabel: planStateShortLabel(
								derivePlanState(m.organization.subscription),
							),
						})),
					effectivePermissions: effectivePermissions.map((p) => ({
						entity: p.entity,
						action: p.action,
						access: p.access,
					})),
				}
			},
			{ timings, type: 'find user', desc: 'find user in root' },
		)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}

	// Redirect authenticated users without an organization to onboarding
	// Skip redirect when impersonating (admin may not have their own org)
	const isAdminUser = user?.roles.some((r) => r.name === 'admin') ?? false
	if (
		user &&
		!user.organization &&
		!isImpersonating &&
		url.pathname.startsWith('/app') &&
		!url.pathname.startsWith('/onboarding')
	) {
		// Site admins without org → send to /admin instead of onboarding
		throw redirect(isAdminUser ? '/admin' : '/onboarding/organization')
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	return data(
		{
			user,
			organization: user?.organization ?? null,
			membership: user?.membership ?? null,
			organizations: user?.organizations ?? [],
			isImpersonating,
			impersonatingOrgName,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	env?: Record<string, string | undefined>
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang="en" className="min-h-full overflow-x-hidden">
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	return (
		<Document nonce={nonce} env={data?.ENV}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	useToast(data.toast)

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			<ImpersonationBanner />
			<div className="flex min-h-screen flex-col justify-between">


				<div className="flex flex-1 flex-col">
					<Outlet />
				</div>


			</div>
			<CookieConsentBanner />
			<EpicToaster closeButton position="top-center" />
			<EpicProgress />
		</OpenImgContextProvider>
	)
}

export { Logo, type LogoVariant } from '#app/components/ui/logo.tsx'

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
